import { ChangeEvent, FC, useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import queryString from 'query-string'
import getByPath from 'lodash/get'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { useSearchParams } from 'react-router-dom'
import { i18n as I18n } from 'builder/utils/i18n'
import { actions } from 'builder/modules/signUp'
import {
  TemplatesV2,
  SocialProfile,
  Introduction,
  ContactInfo,
  Illustrations,
} from 'builder/components/SignUp'
import { ConfigScopesEnum, selectors as appSelectors } from 'builder/modules/init'
import { withRouter } from 'builder/hocs/withRouter'
import { filterGreekTemplates } from 'builder/utils/filterGreekTemplates'
import { ATS_TEMPLATES, JAPANESE_TEMPLATES } from 'builder/components/Helper/constants'
import CookieV4 from 'builder/components/CookieV4'
import ErrorLogger from 'builder/services/ErrorLogger'
import { getRecaptchaLoginActionToken } from 'builder/utils/recapchaActions'
import Spinner from 'builder/components/Spinner'
import { SpinnerWrapper } from 'builder/components/SignUp/Templates/styles'
import { navigate } from 'builder/modules/navigate'
import TemplateGuestProvider, {
  CurrentStepName,
  useMainAppContext,
  useSetCurrentStepName,
  useSetMainProperties,
} from './context/TemplatesGuestContext'
import TemplatesLayout from './TemplatesLayout'
import SingleSignup from 'builder/components/SignUp/SingleSignup'

type DocumentType = 'tr_document' | 'resume' | 'cover_letter'

// TODO: change this to be proper types when

type TemplatesGuestProps = {
  resumeTemplates: any[]
  coverLetterTemplates: any[]
  heroTemplateName: string
  location: Record<string, any>
  signUp: (props: any) => any
  persistSignUpInfo: ({ type, template }: { type: DocumentType; template?: string }) => any
  loadSignUpInfo: ({ type, socialProfile }: { type: DocumentType; socialProfile?: unknown }) => any
  getSignUpPayload: (...args: any[]) => any
  setDocumentTemplate: ({
    type,
    template,
    documentId,
    showReview,
  }: {
    type: DocumentType
    template: string
    documentId: string
    showReview: boolean
  }) => any
  signUpInfo: Record<string, any>
  config: Record<string, any>
  documentType: DocumentType
  locale: string
  resumeTemplateCategories: any[]
  coverLetterTemplateCategories: any[]
}

type TemplateGuestInfo = {
  location: string
  movingDirection: 'forward' | 'backwards'
  introductionErrors: {
    firstName: string
    lastName: string
  }
  contactInfoErrors: {
    email: string
  }
}

const TemplatesGuest: FC<TemplatesGuestProps> = props => {
  const {
    resumeTemplateCategories,
    resumeTemplates,
    coverLetterTemplateCategories,
    coverLetterTemplates,
    config,
    heroTemplateName,
    locale,
    documentType,
    getSignUpPayload,
    persistSignUpInfo,
    loadSignUpInfo,
    setDocumentTemplate,
    signUpInfo,
    signUp,
    location,
  } = props

  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const documentId = searchParams.getAll('documentId')
  const flowStr = (searchParams.has('flow') && searchParams.get('flow')) || undefined
  const hasReview = (searchParams.has('hasReview') && searchParams.get('hasReview')) || undefined

  const [loading, setLoading] = useState<boolean>(true)
  const [info, setInfo] = useState<TemplateGuestInfo>({
    location: '',
    movingDirection: 'forward',
    introductionErrors: {
      firstName: '',
      lastName: '',
    },
    contactInfoErrors: {
      email: '',
    },
  })

  const { currentStep, currentStepName } = useMainAppContext()
  const setTemplateGuestProperties = useSetMainProperties()
  const setCurrentStepPanel = useSetCurrentStepName()
  const templatesSkipVariant = config.features.signUpTemplatesSkip
  const isSuperApp = config?.features?.superApp
  const rtpAtpUnhideCtaAB = config.features.rtpAtpUnhideCta
  const atpStickyCategories = config.features.atpStickyCategories
  const { whitelabeled } = config?.settings || { whitelabeled: false }

  if (isSuperApp) navigate('/create-account')

  // Preserve initial query parameters
  const urlParams = queryString.parse(location.search)
  const regionPrefix = (urlParams.current_region_prefix as string) || ''

  const handleChooseTemplate = (template: string) => {
    dispatch(persistSignUpInfo({ type: documentType, template }))
    if (!documentId) return
    dispatch(
      setDocumentTemplate({
        type: documentType,
        documentId: documentId[0],
        template,
        showReview: hasReview === 'true',
      }),
    )
  }

  const handleSkipTemplate = () => {
    trackInternalEvent('click_skip_template_selection', { label: 'funnel' })
    handleChooseTemplate('london')
  }

  const handleExit = () => {
    trackInternalEvent('click_exit_template_section', { label: 'funnel' })
  }

  const handleBackClick = () => {
    let nextStep = currentStep - 1
    if (nextStep === -1) {
      window.history.back()
      location.reload()
      return // 0 is the first step, no back action
    }

    setTemplateGuestProperties({
      currentStep: nextStep,
      currentStepName: CurrentStepName[nextStep],
    })
  }

  const handleIntroductionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInfo(currentInfo => ({
      ...currentInfo,
      introductionErrors: {
        ...info.introductionErrors,
        [event.target.name]: '',
      },
    }))
  }

  const handleContactInfoChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInfo(currentInfo => ({
      ...currentInfo,
      contactInfoErrors: {
        ...info.contactInfoErrors,
        [event.target.name]: '',
      },
    }))
  }

  const finishSignUp = async () => {
    try {
      const { recaptchaToken, recaptchaAction } = await getRecaptchaLoginActionToken()

      signUp({
        type: documentType,
        isSuperApp,
        ...signUpInfo,
        recaptchaToken,
        recaptchaAction,
        noRedirect: true, // Prevent redirecting to the document editor
      })
    } catch (error) {
      ErrorLogger.log(error)
    }
  }

  const checkFirstAndLastName = () => {
    const isEmptyFirstName = !signUpInfo.firstName.trim().length
    const isEmptyLastName = !signUpInfo.lastName.trim().length

    if (isEmptyFirstName || isEmptyLastName) {
      const errorString = I18n.t('builder.sign_up.field_is_required')
      setInfo(currentInfo => ({
        ...currentInfo,
        introductionErrors: {
          firstName: isEmptyFirstName ? errorString : '',
          lastName: isEmptyLastName ? errorString : '',
        },
      }))
      return false
    }
    return true
  }

  const confirmEmailForm = () => {
    const isEmptyEmail = !signUpInfo.email.trim().length

    if (isEmptyEmail) {
      setInfo(currentInfo => ({
        ...currentInfo,
        contactInfoErrors: {
          email: isEmptyEmail ? I18n.t('builder.sign_up.field_is_required') : '',
        },
      }))
    } else {
      finishSignUp()
    }
  }

  const handleSingleSignUp = async () => {
    persistSignUpInfo({ type: documentType })
    const allOk = checkFirstAndLastName()
    if (!allOk) return
    confirmEmailForm()
    setCurrentStepPanel('templates')
  }

  const moveToNextPageOrFinish = () => {
    switch (currentStepName) {
      case 'introduction': {
        checkFirstAndLastName()
        break
      }
      case 'contact-info': {
        confirmEmailForm()
        break
      }
    }

    setTemplateGuestProperties({
      currentStep: currentStep + 1,
      currentStepName: CurrentStepName[currentStep + 1],
    })
  }

  const handleSocialProfileSkip = () => {
    moveToNextPageOrFinish()
    trackInternalEvent('skip_social_sign_up')
  }

  const handleIntroductionContinue = () => {
    dispatch(persistSignUpInfo({ type: documentType }))
    moveToNextPageOrFinish()
  }

  const { template } = signUpInfo
  const templateId = template || heroTemplateName
  const isResumeFunnel = documentType === 'resume'
  const categories = isResumeFunnel ? resumeTemplateCategories : coverLetterTemplateCategories

  const templatesList = isResumeFunnel
    ? resumeTemplates
    : [
        // TODO after Greek templates release: Remove the condition
        ...(locale === 'gr'
          ? filterGreekTemplates(coverLetterTemplates, locale)
          : coverLetterTemplates),
      ]
  // locale check added to show japanese template
  const isJapaneseLocale = locale === 'ja-JP'
  let templates = templatesList
  if (!isJapaneseLocale) {
    templates = templatesList.filter(value => !JAPANESE_TEMPLATES.includes(value.id))
  } else {
    // TODO: Remove this condition after fixing ATS templates
    templates = templatesList.filter(value => !ATS_TEMPLATES.includes(value.id))
  }

  // edge case if the user lands here from anywhere without following the flow.
  if (flowStr && ['upload', 'signIn'].includes(flowStr) && documentId.length === 0)
    navigate('/', { replace: true })

  useEffect(() => {
    if (documentId && documentId.length > 0) setLoading(false)
  }, [documentId, setLoading])

  useEffect(() => {
    if (flowStr && ['upload', 'signIn'].includes(flowStr))
      setTemplateGuestProperties({ currentStep: 3, currentStepName: 'templates' })
  }, [flowStr, setTemplateGuestProperties])

  useEffect(() => {
    // // Read social authorization data from the config passed from the backend
    const socialProfile = getByPath(props, 'config.socialProfile')
    dispatch(loadSignUpInfo({ type: documentType, socialProfile }))
    dispatch(getSignUpPayload())

    trackMarketingEvent('Sign up', 'Start Funnel')
  }, [])

  return (
    <TemplatesLayout
      regionPrefix={regionPrefix}
      handleSkipButton={handleSkipTemplate}
      handleExitButton={handleExit}
      skipTemplatesVariant={templatesSkipVariant}
      loading={loading}
    >
      {(!templates || templates.length === 0) && (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )}
      {templates.length > 0 && (
        <>
          {currentStepName === 'templates' && (
            <TemplatesV2
              recommended={heroTemplateName}
              templates={templates}
              currentTemplate={template}
              onChoose={handleChooseTemplate}
              documentType={documentType}
              categories={categories}
              loading={loading}
              rtpAtpUnhideCtaAB={rtpAtpUnhideCtaAB}
              atpStickyCategories={atpStickyCategories === 'v1'}
            />
          )}
          {currentStepName === 'social-profile' && (
            <>
              {whitelabeled ? (
                <SingleSignup
                  onBack={() => handleBackClick()}
                  onContinue={() => handleSingleSignUp()}
                  documentType={documentType}
                  onChange={handleContactInfoChange}
                  outsideEmailError={info.contactInfoErrors.email}
                  templateId={templateId}
                  firstNameError={info.introductionErrors.firstName}
                  lastNameError={info.introductionErrors.lastName}
                />
              ) : (
                <SocialProfile
                  onSkip={handleSocialProfileSkip}
                  onBack={() => {}}
                  documentType={documentType}
                  templateId={templateId}
                  skipTemplatesTestEnabled={true}
                />
              )}
            </>
          )}
          {currentStepName === 'introduction' && (
            <Introduction
              onBack={() => handleBackClick()}
              onContinue={handleIntroductionContinue}
              firstNameError={info.introductionErrors.firstName}
              lastNameError={info.introductionErrors.lastName}
              onChange={handleIntroductionChange}
              documentType={documentType}
              templateId={templateId}
            />
          )}
          {currentStepName === 'contact-info' && (
            <ContactInfo
              onBack={() => handleBackClick()}
              onContinue={moveToNextPageOrFinish}
              documentType={documentType}
              onChange={handleContactInfoChange}
              outsideEmailError={info.contactInfoErrors.email}
              templateId={templateId}
            />
          )}
          <Illustrations currentStepName={currentStepName} />
          <CookieV4 />
        </>
      )}
    </TemplatesLayout>
  )
}

const Layout = (props: any) => {
  return (
    <TemplateGuestProvider>
      <TemplatesGuest {...props} />
    </TemplateGuestProvider>
  )
}

// ---
// Connect to data
// ---
const mapStateToProps = (state: any) => ({
  config: appSelectors.config(state, ConfigScopesEnum.signUp),
  resumeTemplates: state.signUp.resumeTemplates,
  coverLetterTemplates: state.signUp.coverLetterTemplates,
  resumeTemplateCategories: state.signUp.resumeTemplateCategories,
  coverLetterTemplateCategories: state.signUp.coverLetterTemplateCategories,
  heroTemplateName: state.signUp.heroTemplateName,
  signUpInfo: state.signUp.signUpInfo,
  locale: state.application.configs.country.locale,
})

// todo: add the get resume and then update the
const mapDispatchToProps = {
  signUp: actions.signUpRequest,
  persistSignUpInfo: actions.persistSignUpInfo,
  loadSignUpInfo: actions.loadSignUpInfo,
  getSignUpPayload: actions.getSignUpPayload,
  setDocumentTemplate: actions.setDocumentTemplate,
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Layout))
